import styled from '@emotion/styled';
import { StaticMap } from '@eventbrite/event-create';
import { GenericLazyString, gettext } from '@eventbrite/i18n';
import {
    CalendarIcon,
    Divider,
    ExpansionPanel,
    ExpansionPanelContent,
    ExpansionPanelTrigger,
    MapPinIcon,
    Typography,
} from '@eventbrite/marmalade';
import { format, isSameDay } from 'date-fns';
import React from 'react';
import { useTransientAutoCreatedEventContext } from '../../../DraftEventStoreProvider';
import { EventDateTime, LocationType } from '../../../shared';
import './EventDate.scss';

export enum EventDateAndLocationContainerTestId {
    Container = 'EventDateAndLocationContainer',
}

const EventLocationOnMap = ({
    location,
    longitude,
    latitude,
}: {
    location: string;
    longitude: string;
    latitude: string;
}) => {
    return (
        <ExpansionPanel variant="ghost">
            <ExpansionPanelTrigger className="expansion-panel-trigger">
                <Typography as="p" color="ui-800" variant="body-md-bold">
                    {gettext('Show map')}
                </Typography>
            </ExpansionPanelTrigger>
            <ExpansionPanelContent>
                <StaticMap
                    location={location}
                    latitude={latitude}
                    longitude={longitude}
                />
            </ExpansionPanelContent>
        </ExpansionPanel>
    );
};

export const EventDateAndLocation = () => {
    const formatEventDate = (
        startTime: Omit<EventDateTime, 'local'>,
        endTime: Omit<EventDateTime, 'local'>,
    ): string => {
        const startDate = new Date(startTime.utc);
        const endDate = new Date(endTime.utc);
        const sameDay = isSameDay(startDate, endDate);

        const dayOfWeek = format(startDate, 'EE');
        const dayOfWeekEnd = format(endDate, 'EE');
        const date = format(startDate, 'MMMM d');
        const startTimeFormatted = format(startDate, 'h:mm aaa');
        const endTimeFormatted = format(endDate, 'h:mm aaa');

        if (sameDay) {
            return `${dayOfWeek}, ${date} · ${startTimeFormatted} - ${endTimeFormatted}`;
        } else {
            const endDateFormatted = format(endDate, 'MMMM d');
            return `${dayOfWeek}, ${date} to ${dayOfWeekEnd}, ${endDateFormatted} · ${startTimeFormatted} to ${endTimeFormatted}`;
        }
    };

    const { transientAutoCreatedEvent } = useTransientAutoCreatedEventContext();

    if (!transientAutoCreatedEvent) return null;

    const { venue } = transientAutoCreatedEvent;

    let locationName;
    let locationAddress;

    if (transientAutoCreatedEvent.location_type === LocationType.Venue) {
        locationName = transientAutoCreatedEvent.venue?.name || '';
        locationAddress =
            transientAutoCreatedEvent.venue?.address?.localizedAddress || '';
    } else if (
        transientAutoCreatedEvent.location_type === LocationType.Online
    ) {
        locationName = gettext('Online event');
    } else {
        locationName = gettext('To be announced');
    }

    return (
        <EventDateAndLocationContainer
            aria-label="The date and location of the event"
            data-testid={EventDateAndLocationContainerTestId.Container}
        >
            <Typography as="p" color="neutral-900" variant="heading-sm">
                {gettext('When and where')}
            </Typography>
            <EventDateAndLocationBody>
                <DateAndLocationItem
                    icon={<CalendarIcon size="small" color="#3659e3" />}
                    title={gettext('Date and time')}
                    subtitle={formatEventDate(
                        transientAutoCreatedEvent.start,
                        transientAutoCreatedEvent.end,
                    )}
                />
                <Divider decorative orientation="vertical" />
                <LocationContainer>
                    <DateAndLocationItem
                        icon={<MapPinIcon size="small" color="#3659e3" />}
                        title={gettext('Location')}
                        subtitle={locationName}
                        secondarySubtitle={locationAddress}
                    >
                        {venue &&
                            transientAutoCreatedEvent.location_type ===
                                LocationType.Venue && (
                                <EventLocationOnMap
                                    location={venue.name}
                                    longitude={venue.address?.longitude || ''}
                                    latitude={venue.address?.latitude || ''}
                                />
                            )}
                    </DateAndLocationItem>
                </LocationContainer>
            </EventDateAndLocationBody>
        </EventDateAndLocationContainer>
    );
};

const DateAndLocationItem = ({
    icon,
    title,
    subtitle,
    secondarySubtitle,
    children,
}: {
    icon: React.ReactNode;
    title: GenericLazyString;
    subtitle: GenericLazyString | string;
    secondarySubtitle?: GenericLazyString | string;
    children?: any;
}) => {
    return (
        <DateAndLocationContainer>
            <IconContainer>{icon}</IconContainer>
            <DateAndLocationBody>
                <Typography as="p" color="ui-800" variant="body-lg">
                    {title}
                </Typography>
                <Typography as="p" color="ui-800" variant="body-md-bold">
                    {subtitle}
                </Typography>
                {secondarySubtitle && (
                    <Typography as="p" color="ui-600" variant="body-md">
                        {secondarySubtitle}
                    </Typography>
                )}
                {children}
            </DateAndLocationBody>
        </DateAndLocationContainer>
    );
};

const EventDateAndLocationContainer = styled.section`
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

const EventDateAndLocationBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 32px;
    @media (max-width: 768px) {
        flex-direction: column;
        gap: 16px;
    }
`;

const IconContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 8px;
    background-color: #f8f7fa;
`;

const DateAndLocationContainer = styled.div`
    display: flex;
    gap: 16px;
    width: 100%;
`;

const DateAndLocationBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
`;

const LocationContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;
